import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  Download,
} from '@mui/icons-material';
import { customerService } from '../services/customerService';

interface CustomerUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CustomerUpload: React.FC<CustomerUploadProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    imported: number;
    failed: number;
    errors: Array<{ row?: number; name?: string; error: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const result = await customerService.uploadCSV(file);
      setUploadResult(result);
      if (result.imported > 0 && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({
        imported: 0,
        failed: 0,
        errors: [{ error: 'Upload failed. Please check your file and try again.' }],
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const template = `Dealer,Customer Name,Email,Phone,Vehicle Model,Vehicle Year,Mileage at Sale,Purchase Year,VIN,Notes,Sale Price
Example Dealer,John Doe,john@example.com,+447700900123,Hyundai Tucson,2023,15000,2023,KMHXXXXXXXXXXXXXX,First-time buyer,35000
Example Dealer,Jane Smith,jane@example.com,+447700900456,Hyundai Ioniq 5,2024,5000,2024,KMHYYYYYYYYYYYYYY,Fleet purchase,45000`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload Customers
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Instructions */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Upload your customer data from a CSV file</strong>
            </Typography>
            <Typography variant="caption" component="div">
              • File must be in CSV format
              <br />
              • Supports Mooneys Hyundai DMS export format
              <br />
              • Required columns: Customer Name, Email or Phone
              <br />
              • Optional: Vehicle info, purchase details, notes
            </Typography>
          </Alert>

          {/* Template Download */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadTemplate}
              size="small"
            >
              Download CSV Template
            </Button>
          </Box>

          {/* File Selection */}
          <Paper
            sx={{
              p: 3,
              border: '2px dashed #ccc',
              borderRadius: 2,
              bgcolor: '#fafafa',
              textAlign: 'center',
              cursor: 'pointer',
              mb: 3,
              '&:hover': {
                borderColor: '#1976d2',
                bgcolor: '#f5f5f5',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <Box>
                <Description sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  Click to select CSV file
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or drag and drop here
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Uploading customers...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Upload Results */}
          {uploadResult && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {uploadResult.imported > 0 && (
                  <Chip
                    icon={<CheckCircle />}
                    label={`${uploadResult.imported} customers imported`}
                    color="success"
                    variant="outlined"
                  />
                )}
                {uploadResult.failed > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${uploadResult.failed} failed`}
                    color="error"
                    variant="outlined"
                  />
                )}
              </Box>

              {uploadResult.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Import Errors:</strong>
                  </Typography>
                  <List dense>
                    {uploadResult.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={
                            error.row
                              ? `Row ${error.row}: ${error.error}`
                              : error.name
                              ? `${error.name}: ${error.error}`
                              : error.error
                          }
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              {uploadResult.imported > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Successfully imported {uploadResult.imported} customers.
                    They are now available for outreach campaigns.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          startIcon={<CloudUpload />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerUpload;